import React, { useState, useEffect } from 'react'
import {
  Tag,
  Space,
  Button,
  Modal,
  Typography,
  Descriptions,
  message,
  Input
} from 'antd'
import api from '../../utils/api'
import AdminTable from '../../components/common/AdminTable'
import styled from 'styled-components'

// 莫兰迪色系配色
const colors = {
  cardBackground: '#faf9f6',
  border: 'rgba(168, 181, 192, 0.3)',
  shadow: 'rgba(154, 163, 175, 0.2)',
  textPrimary: '#5a5a5a',
  textSecondary: '#8a8a8a'
}

const StyledCard = styled.div`
  background: ${colors.cardBackground};
  border-radius: 16px;
  box-shadow: 0 4px 16px ${colors.shadow};
  border: 1px solid ${colors.border};
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 16px 12px;
    border-radius: 12px;
  }
`

const { Title } = Typography
const { TextArea } = Input

const StoreApplications = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [selectedStore, setSelectedStore] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [reviewLoading, setReviewLoading] = useState(false)
  const [reviewNote, setReviewNote] = useState('')
  const [provisionAccount, setProvisionAccount] = useState('')
  const [tablePagination, setTablePagination] = useState({
    currentPage: 1,
    itemsPerPage: 10
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await api.get('/api/admin/sales')
      setData(response.data.data)
    } catch (error) {
      console.error('獲取數據失敗:', error)
    }
    setLoading(false)
  }

  const getReviewTag = (status) => {
    if (status === 'approved') return <Tag color='success'>已通過</Tag>
    if (status === 'rejected') return <Tag color='error'>已拒絕</Tag>
    return <Tag color='processing'>待審核</Tag>
  }

  const handleReview = async (reviewStatus) => {
    if (!selectedStore?._id) return
    if (reviewStatus === 'approved' && !provisionAccount.trim()) {
      message.warning('通過上架前，請先填寫店家帳號')
      return
    }
    setReviewLoading(true)
    try {
      const response = await api.put(`/api/admin/sales/${selectedStore._id}/review`, {
        reviewStatus,
        reviewNote,
        account: provisionAccount.trim()
      })

      if (response.data?.success) {
        message.success('審核狀態已更新')
        setModalVisible(false)
        setSelectedStore(null)
        setReviewNote('')
        setProvisionAccount('')
        await fetchData()
      } else {
        message.error(response.data?.message || '審核失敗')
      }
    } catch (error) {
      message.error(error.response?.data?.message || '審核失敗')
    } finally {
      setReviewLoading(false)
    }
  }

  const handleTableChange = (pagination) => {
    setTablePagination((prev) => ({
      ...prev,
      currentPage: pagination.current || prev.currentPage,
      itemsPerPage: pagination.pageSize || prev.itemsPerPage
    }))
  }

  const columns = [
    {
      title: '店家名稱',
      dataIndex: 'storeName',
      key: 'storeName'
    },
    {
      title: '聯絡人',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '行業',
      dataIndex: 'industry',
      key: 'industry'
    },
    {
      title: '地區',
      render: (_, record) => `${record.city} ${record.district}`
    },
    {
      title: '申請狀態',
      dataIndex: 'storeStatus',
      key: 'storeStatus'
    },
    {
      title: '審核狀態',
      dataIndex: 'reviewStatus',
      key: 'reviewStatus',
      render: (status) => getReviewTag(status)
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size='middle'>
          <Button
            type='primary'
            onClick={() => {
              setSelectedStore(record)
              setProvisionAccount(record.provisionedAccount || '')
              setReviewNote(record.reviewNote || '')
              setModalVisible(true)
            }}
          >
            查看詳情
          </Button>
        </Space>
      )
    }
  ]

  return (
    <StyledCard>
      <Title level={2} style={{ marginBottom: 24, color: colors.textPrimary }}>
        店家申請管理
      </Title>
      <AdminTable
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey='_id'
        onChange={handleTableChange}
        pagination={{
          currentPage: tablePagination.currentPage,
          totalItems: data.length,
          itemsPerPage: tablePagination.itemsPerPage
        }}
      />

      <Modal
        title='店家詳細信息'
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedStore && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label='店家名稱'>
              {selectedStore.storeName}
            </Descriptions.Item>
            <Descriptions.Item label='聯絡人'>
              {selectedStore.name}
            </Descriptions.Item>
            <Descriptions.Item label='電話'>
              {selectedStore.phone}
            </Descriptions.Item>
            <Descriptions.Item label='Email'>
              {selectedStore.email}
            </Descriptions.Item>
            <Descriptions.Item label='Line ID'>
              {selectedStore.lineId}
            </Descriptions.Item>
            <Descriptions.Item label='地址'>{`${selectedStore.city}${selectedStore.district}`}</Descriptions.Item>
            <Descriptions.Item label='行業'>
              {selectedStore.industry}
            </Descriptions.Item>
            <Descriptions.Item label='預約時間'>
              {selectedStore.preferredTime}
            </Descriptions.Item>
            <Descriptions.Item label='店家詳情'>
              {selectedStore.storeDetails}
            </Descriptions.Item>
            <Descriptions.Item label='審核狀態'>
              {getReviewTag(selectedStore.reviewStatus)}
            </Descriptions.Item>
            <Descriptions.Item label='審核備註'>
              {selectedStore.reviewNote || '-'}
            </Descriptions.Item>
            <Descriptions.Item label='開通帳號'>
              {selectedStore.provisionedAccount || '-'}
            </Descriptions.Item>
            <Descriptions.Item label='開通密碼'>
              {selectedStore.generatedPassword || '-'}
            </Descriptions.Item>
            <Descriptions.Item label='開通時間'>
              {selectedStore.provisionedAt || '-'}
            </Descriptions.Item>
            <Descriptions.Item label='申請時間'>
              {selectedStore.createdAt}
            </Descriptions.Item>
          </Descriptions>
        )}
        {selectedStore && (
          <div style={{ marginTop: 16 }}>
            <Input
              style={{ marginBottom: 12 }}
              placeholder='請輸入要建立的店家帳號（例：abc.studio）'
              value={provisionAccount}
              onChange={(e) => setProvisionAccount(e.target.value)}
              disabled={selectedStore.reviewStatus === 'approved'}
            />
            <TextArea
              rows={3}
              placeholder='可填寫審核備註（例如不符合上架條件原因）'
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
            />
            <Space style={{ marginTop: 12, width: '100%', justifyContent: 'flex-end' }}>
              <Button
                loading={reviewLoading}
                onClick={() => handleReview('rejected')}
                danger
              >
                拒絕
              </Button>
              <Button
                loading={reviewLoading}
                type='primary'
                onClick={() => handleReview('approved')}
                disabled={selectedStore.reviewStatus === 'approved'}
              >
                通過上架
              </Button>
            </Space>
          </div>
        )}
      </Modal>
    </StyledCard>
  )
}

export default StoreApplications
